import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { UtsavNewPropertyDialogComponent } from '../utsav-new-property-dialog/utsav-new-property-dialog.component';

// manage a utsav's inventory target + property/room assignments.
// data = { utsavId, name }.
@Component({
  selector: 'app-utsav-manage-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    LoaderComponent,
  ],
  templateUrl: './utsav-manage-dialog.component.html',
  styleUrls: ['./utsav-manage-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class UtsavManageDialogComponent implements OnInit {
  inventory: FormGroup;
  propertyToAssign = new FormControl<number | null>(null);

  properties: any[] = [];
  rooms: any[] = [];
  assignableProperties: any[] = [];
  capacity: any = { target: null, assigned: { total: 0, individual: 0, family: 0, bulk: 0 } };

  isLoading: boolean = false;
  private changed: boolean = false;

  // per-property "add rooms" flow
  addRoomsPropertyId: number | null = null;
  addableRooms: any[] = [];
  selectedRoomIds: number[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<UtsavManageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { utsavId: number; name: string },
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private dialog: MatDialog
  ) {
    this.inventory = this.fb.group({
      totalCapacity: [0, [Validators.required, Validators.min(0)]],
      individualCapacity: [0, [Validators.required, Validators.min(0)]],
      familyCapacity: [0, [Validators.required, Validators.min(0)]],
      bulkCapacity: [0, [Validators.required, Validators.min(0)]],
    });
  }

  ngOnInit(): void {
    this.loadAssignments();
    this.loadAssignable();
  }

  loadAssignments(): void {
    this.isLoading = true;
    this.establishmentService.getUtsavAssignments(this.data.utsavId).subscribe({
      next: (res: any) => {
        const data = res?.data || {};
        this.properties = data.properties || [];
        this.rooms = data.rooms || [];
        this.capacity = data.capacity || this.capacity;
        const target = this.capacity.target;
        if (target) {
          this.inventory.patchValue({
            totalCapacity: target.totalCapacity ?? 0,
            individualCapacity: target.individualCapacity ?? 0,
            familyCapacity: target.familyCapacity ?? 0,
            bulkCapacity: target.bulkCapacity ?? 0,
          });
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load assignments.');
      },
    });
  }

  loadAssignable(): void {
    this.establishmentService.getAssignableProperties(this.data.utsavId).subscribe({
      next: (res: any) => (this.assignableProperties = res?.data?.properties || []),
      error: (err) =>
        this.toastService.error(err?.error?.message || 'Failed to load assignable properties.'),
    });
  }

  roomsForProperty(propertyId: number): any[] {
    return this.rooms.filter((room) => Number(room.propertyId) === Number(propertyId));
  }

  isActive(status: unknown): boolean {
    return Number(status) === 1;
  }

  isAddingRoomsFor(propertyId: number): boolean {
    return Number(this.addRoomsPropertyId) === Number(propertyId);
  }

  saveInventory(): void {
    if (this.inventory.invalid) {
      this.inventory.markAllAsTouched();
      this.toastService.error('Capacities must be zero or more.');
      return;
    }
    this.isLoading = true;
    this.establishmentService.setInventoryTarget(this.data.utsavId, this.inventory.value).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Inventory target saved.');
        this.changed = true;
        this.loadAssignments();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to save inventory target.');
      },
    });
  }

  assignProperty(): void {
    const propertyId = Number(this.propertyToAssign.value);
    if (!propertyId) {
      this.toastService.error('Select a property to assign.');
      return;
    }
    this.isLoading = true;
    this.establishmentService.assignProperty(this.data.utsavId, propertyId).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Property assigned.');
        this.changed = true;
        this.propertyToAssign.setValue(null);
        this.loadAssignments();
        this.loadAssignable();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to assign property.');
      },
    });
  }

  unassignProperty(propertyId: number): void {
    propertyId = Number(propertyId);
    this.isLoading = true;
    this.establishmentService.unassignProperty(this.data.utsavId, propertyId).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Property unassigned.');
        this.changed = true;
        this.loadAssignments();
        this.loadAssignable();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to unassign property.');
      },
    });
  }

  // start adding existing rooms of an assigned property: list the property's
  // active rooms that aren't already assigned to this utsav.
  startAddRooms(propertyId: number): void {
    propertyId = Number(propertyId);
    this.addRoomsPropertyId = propertyId;
    this.selectedRoomIds = [];
    this.addableRooms = [];
    const assignedIds = new Set(this.rooms.map((room) => Number(room.roomId)));
    this.establishmentService.listRooms(propertyId).subscribe({
      next: (res: any) => {
        if (!this.isAddingRoomsFor(propertyId)) return;
        const rooms = res?.data?.rooms || [];
        this.addableRooms = rooms.filter(
          (room: any) => this.isActive(room.status) && !assignedIds.has(Number(room.roomId)),
        );
      },
      error: (err) => this.toastService.error(err?.error?.message || 'Failed to load rooms.'),
    });
  }

  toggleRoomSelection(roomId: number, event: Event): void {
    roomId = Number(roomId);
    const checked = (event.target as HTMLInputElement).checked;
    if (checked && !this.selectedRoomIds.some((id) => Number(id) === roomId)) {
      this.selectedRoomIds.push(roomId);
    } else if (!checked) {
      this.selectedRoomIds = this.selectedRoomIds.filter((id) => Number(id) !== roomId);
    }
  }

  confirmAddRooms(): void {
    if (!this.selectedRoomIds.length) {
      this.toastService.error('Select at least one room.');
      return;
    }
    this.isLoading = true;
    this.establishmentService.addRoomsToUtsav(this.data.utsavId, this.selectedRoomIds).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Rooms added.');
        this.changed = true;
        this.cancelAddRooms();
        this.loadAssignments();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to add rooms.');
      },
    });
  }

  cancelAddRooms(): void {
    this.addRoomsPropertyId = null;
    this.addableRooms = [];
    this.selectedRoomIds = [];
  }

  addNewProperty(): void {
    const ref = this.dialog.open(UtsavNewPropertyDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { utsavId: this.data.utsavId },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) {
        this.changed = true;
        this.loadAssignments();
        this.loadAssignable();
      }
    });
  }

  unassignRoom(roomId: number): void {
    roomId = Number(roomId);
    this.isLoading = true;
    this.establishmentService.unassignRoom(this.data.utsavId, roomId).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Room unassigned.');
        this.changed = true;
        this.loadAssignments();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to unassign room.');
      },
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
